import React from "react";
import { DatePicker } from "@mui/x-date-pickers";
import { LocalizationProvider } from "@mui/x-date-pickers/LocalizationProvider";
import { AdapterDayjs } from "@mui/x-date-pickers/AdapterDayjs";
import dayjs from "dayjs";
import customParseFormat from "dayjs/plugin/customParseFormat";

dayjs.extend(customParseFormat);

const CustomDateField = ({
  label,
  name,
  value,
  onChange,
  required = false,
  error = false,
  helperText = "",
  format = "DD/MM/YYYY",
  disabledAfter = null,
  fullWidth = true,
  size = "small",
  placeholder = "",
  disabled = false,
  ...props
}) => {
  const dateValue = React.useMemo(() => {
    if (!value) return null;
    const parsed = dayjs(value, ["YYYY-MM-DD", "DD/MM/YYYY"]);
    return parsed.isValid() ? parsed : null;
  }, [value]);

  const handleDateChange = (newValue) => {
    if (!newValue || !newValue.isValid) {
      onChange({
        target: {
          name: name,
          value: "",
        },
      });
      return;
    }
    
    const formattedValue = newValue.format("YYYY-MM-DD");
    onChange({
      target: {
        name: name,
        value: formattedValue,
      },
    });
  };

  return (
    <LocalizationProvider dateAdapter={AdapterDayjs}>
      <DatePicker
        label={label}
        name={name}
        value={dateValue}
        onChange={handleDateChange}
        format={format}
        disabled={disabled}
        slotProps={{
          field: { clearable: true },
          actionBar: {
            actions: ['clear', 'today', 'cancel', 'accept'],
          },
          textField: {
            required,
            error,
            helperText,
            fullWidth,
            size,
            placeholder,
            sx: {
              minWidth: 200,
              borderRadius: 1,
              "& .MuiOutlinedInput-root": {
                borderRadius: "8px",
                backgroundColor: disabled ? "#F1F5F9" : "#FFFFFF",
                "&:hover fieldset": {
                  borderColor: error ? "#EF4444" : "#2469F0",
                },
                "& fieldset": {
                  borderColor: error ? "#EF4444" : "#E2E8F0",
                },
                "&.Mui-focused fieldset": {
                  borderColor: error ? "#EF4444" : "#2469F0",
                  borderWidth: 2,
                },
              },
              "& .MuiInputLabel-root": {
                fontSize: "0.75rem",
                fontWeight: 500,
                color: "#475467",
                "&.Mui-focused": {
                  color: "#2469F0",
                },
                "&.Mui-error": {
                  color: "#EF4444",
                },
              },
              "& .MuiInputBase-input": {
                fontSize: "0.85rem",
                padding: "10px 14px",
                "&::placeholder": {
                  color: "#94A3B8",
                  opacity: 1,
                },
              },
              "& .MuiFormHelperText-root": {
                fontSize: "0.7rem",
                fontWeight: 500,
                marginLeft: 0,
                marginTop: "4px",
                color: error ? "#EF4444" : "#94A3B8",
              },
            },
            InputLabelProps: { shrink: true },
            inputProps: {
              max: disabledAfter || undefined,
              placeholder: placeholder || format,
            },
            ...props,
          },
        }}
      />
    </LocalizationProvider>
  );
};

export default CustomDateField;